import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  CalendarRange, Plus, Trash2, Pencil, ChevronLeft, ChevronRight,
  Sparkles, Percent, DollarSign, Info, ShieldAlert, Globe, TrendingUp, X, Copy,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const MONTH_NAMES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const WEEKDAY_SHORT = ["D", "S", "T", "Q", "Q", "S", "S"];

// Deterministic palette – rules get colored by insertion order in the active list.
const RULE_COLORS = [
  { bg: "bg-emerald-500", text: "text-white", ring: "ring-emerald-300", chip: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { bg: "bg-sky-500",     text: "text-white", ring: "ring-sky-300",     chip: "bg-sky-50 text-sky-800 border-sky-200" },
  { bg: "bg-amber-500",   text: "text-white", ring: "ring-amber-300",   chip: "bg-amber-50 text-amber-900 border-amber-200" },
  { bg: "bg-rose-500",    text: "text-white", ring: "ring-rose-300",    chip: "bg-rose-50 text-rose-800 border-rose-200" },
  { bg: "bg-violet-500",  text: "text-white", ring: "ring-violet-300",  chip: "bg-violet-50 text-violet-800 border-violet-200" },
  { bg: "bg-indigo-500",  text: "text-white", ring: "ring-indigo-300",  chip: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  { bg: "bg-teal-500",    text: "text-white", ring: "ring-teal-300",    chip: "bg-teal-50 text-teal-800 border-teal-200" },
  { bg: "bg-fuchsia-500", text: "text-white", ring: "ring-fuchsia-300", chip: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200" },
];

const EMPTY_RULE = {
  label: "",
  scope_type: "global",
  scope_id: null,
  date_start: "",
  date_end: "",
  adjustment_type: "percent",
  adjustment_value: 0,
  min_price: "",
  max_price: "",
  priority: 0,
};

function fmtBRL(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/** Mirror of pricing_engine.compute_nightly — pure client-side calc for tooltips
 *  and stats. NEVER used to submit prices. All authoritative pricing still
 *  goes through the backend (compute_base_total via /preview or /availability).
 */
function computeNightlyLocal(basePrice, rule) {
  const base = Number(basePrice) || 0;
  if (!rule) return base;
  const adj = Number(rule.adjustment_value) || 0;
  let nightly = base;
  if (rule.adjustment_type === "percent") nightly = base * (1 + adj / 100);
  else if (rule.adjustment_type === "fixed") nightly = base + adj;
  if (rule.min_price != null && nightly < Number(rule.min_price)) nightly = Number(rule.min_price);
  if (rule.max_price != null && nightly > Number(rule.max_price)) nightly = Number(rule.max_price);
  if (nightly < 0) nightly = 0;
  return nightly;
}

function daysInMonth(year, monthIdx) {
  return new Date(year, monthIdx + 1, 0).getDate();
}

/** Desloca uma data ISO em exatamente +1 ano, corrigindo 29/fev → 28/fev
 *  quando o ano destino não é bissexto. Pura, sem timezone. */
function shiftDateByOneYear(iso) {
  if (!iso || typeof iso !== "string") return iso;
  const [y, m, d] = iso.split("-").map(Number);
  const newYear = y + 1;
  // Corrige 29/02 em anos não bissextos (regra: div 4, exceto séc não-div 400)
  const isLeap = (yr) => (yr % 4 === 0 && yr % 100 !== 0) || yr % 400 === 0;
  const maxDay = (m === 2 && !isLeap(newYear)) ? 28 : d;
  return `${newYear}-${String(m).padStart(2, "0")}-${String(maxDay).padStart(2, "0")}`;
}

/** Incrementa o ano no rótulo de forma robusta:
 *  - Se o rótulo contiver algum ano 20XX (ex.: "verão 2027", "carnaval 2028"),
 *    incrementa todos os anos encontrados em +1 ano.
 *  - Se contiver intervalo tipo "26/27" ou "2026/2027", desloca ambos.
 *  - Fallback: anexa " (YYYY)" com o ano de início da nova regra. */
function incrementYearInLabel(label, newStartIso) {
  const safe = (label || "").trim();
  const yr = Number((newStartIso || "").slice(0, 4));
  if (!safe) return `Cópia ${yr}`;
  // Intervalo com barra tem prioridade (evita colisão com anos soltos)
  const rxRange4 = /\b(20\d{2})\/(20\d{2})\b/;
  const m4 = safe.match(rxRange4);
  if (m4) {
    return safe.replace(rxRange4, `${Number(m4[1]) + 1}/${Number(m4[2]) + 1}`);
  }
  const rxRange2 = /\b(\d{2})\/(\d{2})\b/;
  const m2 = safe.match(rxRange2);
  if (m2) {
    const a = (Number(m2[1]) + 1) % 100, b = (Number(m2[2]) + 1) % 100;
    return safe.replace(rxRange2, `${String(a).padStart(2, "0")}/${String(b).padStart(2, "0")}`);
  }
  // Qualquer ano 20XX solto → incrementa (cobre "verão 2027" → "verão 2028")
  const rxAny4 = /\b(20\d{2})\b/g;
  if (rxAny4.test(safe)) {
    return safe.replace(/\b(20\d{2})\b/g, (m) => String(Number(m) + 1));
  }
  // Fallback: sufixo com o ano novo
  return `${safe} (${yr})`;
}

function isoDate(year, monthIdx, day) {
  const m = String(monthIdx + 1).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
}

function dateInRange(iso, start, end) {
  return iso >= start && iso <= end;
}

/** Resolve the winning rule for a specific date using scope priority (house > group > global) and priority number. */
function resolveRuleForDate(rules, iso, house) {
  const candidates = rules.filter((r) => r.active && dateInRange(iso, r.date_start, r.date_end));
  if (candidates.length === 0) return null;
  // Filter by scope applicability if a house is selected.
  const applicable = candidates.filter((r) => {
    if (r.scope_type === "global") return true;
    if (!house) return true; // annual overview shows all
    if (r.scope_type === "house") return r.scope_id === house.id;
    if (r.scope_type === "group") return r.scope_id === house.group_id;
    return false;
  });
  if (applicable.length === 0) return null;
  // Winner: house > group > global, tie-break by priority DESC, then created_at DESC.
  const scopeRank = { house: 3, group: 2, global: 1 };
  applicable.sort((a, b) => {
    const s = scopeRank[b.scope_type] - scopeRank[a.scope_type];
    if (s !== 0) return s;
    const p = (b.priority || 0) - (a.priority || 0);
    if (p !== 0) return p;
    return String(b.created_at || "").localeCompare(String(a.created_at || ""));
  });
  return applicable[0];
}

export default function AdminSeasonalPricing() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [rules, setRules] = useState([]);
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewHouseId, setPreviewHouseId] = useState("");
  const [editing, setEditing] = useState(null); // rule being created/edited (object) or null

  const ruleColorMap = useMemo(() => {
    const map = {};
    rules.forEach((r, i) => { map[r.id] = RULE_COLORS[i % RULE_COLORS.length]; });
    return map;
  }, [rules]);

  const selectedHouse = useMemo(
    () => houses.find((h) => h.id === previewHouseId) || null,
    [houses, previewHouseId],
  );

  async function loadRules() {
    const r = await api.get("/admin/seasonal-pricing/rules", {
      params: { include_inactive: false },
    });
    setRules(r.data || []);
  }

  async function loadHouses() {
    const r = await api.get("/admin/houses");
    setHouses(r.data || []);
  }

  async function loadAll() {
    setLoading(true);
    try {
      await Promise.all([loadRules(), loadHouses()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar dados.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  function openNew() {
    setEditing({ ...EMPTY_RULE, _new: true });
  }

  function openEdit(rule) {
    setEditing({
      ...rule,
      min_price: rule.min_price ?? "",
      max_price: rule.max_price ?? "",
      _new: false,
    });
  }

  /**
   * Duplica uma regra: pré-preenche o modal como REGRA NOVA (nenhuma
   * gravação ainda) com datas deslocadas +1 ano e o rótulo com o ano
   * incrementado. Todos os demais campos (escopo, tipo, valor, min/max,
   * prioridade) são preservados para o admin revisar.
   */
  function openDuplicate(rule) {
    const shift = shiftDateByOneYear;
    const newStart = shift(rule.date_start);
    const newEnd = shift(rule.date_end);
    const newLabel = incrementYearInLabel(rule.label, newStart);
    setEditing({
      // não passa id → o modal grava como criação
      label: newLabel,
      scope_type: rule.scope_type,
      scope_id: rule.scope_id,
      date_start: newStart,
      date_end: newEnd,
      adjustment_type: rule.adjustment_type,
      adjustment_value: rule.adjustment_value,
      min_price: rule.min_price ?? "",
      max_price: rule.max_price ?? "",
      priority: rule.priority ?? 0,
      _new: true,
      _duplicated_from: rule.id,
    });
    toast.info("Regra pré-preenchida com datas do ano seguinte. Revise e clique em Criar regra.");
  }

  async function onDeactivate(rule) {
    if (!window.confirm(`Desativar a regra "${rule.label}"? Esta ação não altera reservas já confirmadas.`)) return;
    try {
      await api.delete(`/admin/seasonal-pricing/rules/${rule.id}`);
      toast.success("Regra desativada.");
      await loadRules();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao desativar regra.");
    }
  }

  if (loading) {
    return <div className="text-slate-500 text-sm" data-testid="seasonal-loading">Carregando…</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-seasonal-pricing">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-slate-500">
            Configurações → Preços
          </div>
          <h1 className="font-display text-3xl text-[#22302E] mt-1 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#0AA89C]" />
            Motor de Precificação Sazonal
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Crie regras (globais, por casa ou grupo) que ajustam o preço base por percentual
            ou valor fixo dentro de um intervalo de datas. Regras de <strong>casa</strong>{" "}
            têm precedência sobre grupo, e grupo sobre global; empates são resolvidos
            pela <strong>prioridade</strong>. <span className="text-slate-500">Reservas já confirmadas nunca são alteradas.</span>
          </p>
        </div>
        <Button
          onClick={openNew}
          data-testid="seasonal-new-rule-btn"
          className="bg-[#0AA89C] hover:bg-[#088c82] text-white"
        >
          <Plus className="w-4 h-4 mr-2" /> Nova regra
        </Button>
      </div>

      <div className="grid xl:grid-cols-[minmax(0,1fr)_380px] gap-6">
        {/* Left: calendar */}
        <section
          className="bg-white border border-slate-200 rounded-md p-6"
          data-testid="seasonal-calendar-panel"
        >
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-3">
              <CalendarRange className="w-5 h-5 text-[#0AA89C]" />
              <h2 className="font-semibold text-[#22302E]">Visão anual · {year}</h2>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => setYear((y) => y - 1)}
                data-testid="seasonal-year-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-medium w-16 text-center">{year}</span>
              <Button
                variant="outline" size="sm"
                onClick={() => setYear((y) => y + 1)}
                data-testid="seasonal-year-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() => setYear(new Date().getFullYear())}
                data-testid="seasonal-year-current"
              >
                Hoje
              </Button>
            </div>
          </div>

          <div className="mb-4 flex items-center gap-2">
            <Label className="text-xs text-slate-500">Filtrar por casa (opcional)</Label>
            <select
              className="border border-slate-200 rounded px-2 py-1 text-sm bg-white"
              value={previewHouseId}
              onChange={(e) => setPreviewHouseId(e.target.value)}
              data-testid="seasonal-house-filter"
            >
              <option value="">Todas (visão global)</option>
              {houses.map((h) => (
                <option key={h.id} value={h.id}>{h.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {MONTH_NAMES.map((mName, mIdx) => (
              <MonthGrid
                key={mIdx}
                year={year} monthIdx={mIdx} monthName={mName}
                rules={rules} colorMap={ruleColorMap} house={selectedHouse}
              />
            ))}
          </div>

          {selectedHouse && (
            <HouseStatsPanel house={selectedHouse} rules={rules} />
          )}

          {rules.length === 0 && (
            <div className="mt-6 p-4 bg-slate-50 border border-dashed border-slate-300 rounded text-sm text-slate-600 flex items-center gap-2">
              <Info className="w-4 h-4 text-slate-400" />
              Nenhuma regra ativa. Clique em <strong className="mx-1">Nova regra</strong> para começar.
            </div>
          )}
        </section>

        {/* Right: rule list */}
        <section
          className="bg-white border border-slate-200 rounded-md p-6 h-fit"
          data-testid="seasonal-rules-panel"
        >
          <h2 className="font-semibold text-[#22302E] mb-4">Regras ativas</h2>

          {rules.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhuma regra ativa.</p>
          ) : (
            <ul className="space-y-3">
              {rules.map((r) => {
                const c = ruleColorMap[r.id] || RULE_COLORS[0];
                const houseName = r.scope_type === "house"
                  ? (houses.find((h) => h.id === r.scope_id)?.name || "(casa removida)")
                  : null;
                // Pick a house for the public verification call:
                // rule scope=house → that house; otherwise → currently filtered → first available.
                const verifyHouse = r.scope_type === "house"
                  ? houses.find((h) => h.id === r.scope_id)
                  : (selectedHouse || houses[0]);
                return (
                  <li
                    key={r.id}
                    className={`p-3 border rounded-md ${c.chip}`}
                    data-testid={`seasonal-rule-item-${r.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-[#22302E] truncate">{r.label}</div>
                        <div className="text-xs text-slate-600 mt-0.5">
                          {r.date_start} → {r.date_end}
                        </div>
                        <div className="text-xs mt-1">
                          <span className="uppercase tracking-widest text-[10px] text-slate-500 mr-1">Escopo:</span>
                          {r.scope_type === "global" && "Global"}
                          {r.scope_type === "group" && `Grupo ${r.scope_id || ""}`}
                          {r.scope_type === "house" && (houseName || "Casa")}
                        </div>
                        <div className="text-xs mt-1 flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center gap-1 font-medium">
                            {r.adjustment_type === "percent"
                              ? <><Percent className="w-3 h-3" /> {r.adjustment_value}%</>
                              : <><DollarSign className="w-3 h-3" /> {fmtBRL(r.adjustment_value)}</>}
                          </span>
                          <span className="text-slate-500">· prioridade {r.priority || 0}</span>
                        </div>
                        {(r.min_price || r.max_price) && (
                          <div className="text-[11px] text-slate-500 mt-1">
                            {r.min_price != null ? `mín ${fmtBRL(r.min_price)}` : ""}
                            {r.min_price != null && r.max_price != null ? " · " : ""}
                            {r.max_price != null ? `máx ${fmtBRL(r.max_price)}` : ""}
                          </div>
                        )}
                        <PublicVerification rule={r} house={verifyHouse} />
                      </div>
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => openEdit(r)}
                          className="p-1.5 rounded hover:bg-white/60"
                          title="Editar"
                          data-testid={`seasonal-rule-edit-${r.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => openDuplicate(r)}
                          className="p-1.5 rounded hover:bg-white/60 text-[#066B63]"
                          title="Duplicar para o ano seguinte"
                          data-testid={`seasonal-rule-duplicate-${r.id}`}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => onDeactivate(r)}
                          className="p-1.5 rounded hover:bg-white/60 text-rose-700"
                          title="Desativar"
                          data-testid={`seasonal-rule-delete-${r.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      {/* Modal */}
      {editing && (
        <RuleModal
          rule={editing}
          houses={houses}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await loadRules();
          }}
        />
      )}
    </div>
  );
}

/* ============================== MonthGrid ============================== */
function MonthGrid({ year, monthIdx, monthName, rules, colorMap, house }) {
  const numDays = daysInMonth(year, monthIdx);
  const firstDow = new Date(year, monthIdx, 1).getDay(); // 0=Sun
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= numDays; d++) cells.push(d);

  return (
    <div className="border border-slate-200 rounded-md p-3" data-testid={`seasonal-month-${monthIdx + 1}`}>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
        {monthName}
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-[10px] text-slate-400 mb-1">
        {WEEKDAY_SHORT.map((w, i) => (
          <div key={i} className="text-center">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} className="aspect-square" />;
          const iso = isoDate(year, monthIdx, d);
          const rule = resolveRuleForDate(rules, iso, house);
          const c = rule ? colorMap[rule.id] : null;
          // Rich multi-line tooltip (browsers render \n in title attribute).
          const lines = [`Data: ${iso}`];
          if (house) {
            const base = Number(house.base_price) || 0;
            const nightly = computeNightlyLocal(base, rule);
            lines.push(`Preço base: ${fmtBRL(base)}`);
            if (rule) {
              lines.push(`Preço final: ${fmtBRL(nightly)}`);
              const adj = rule.adjustment_type === "percent"
                ? `${rule.adjustment_value}%`
                : fmtBRL(rule.adjustment_value);
              lines.push(`Regra: ${rule.label} (${adj})`);
            } else {
              lines.push("Sem regra ativa");
            }
          } else if (rule) {
            const adj = rule.adjustment_type === "percent"
              ? `${rule.adjustment_value}%`
              : fmtBRL(rule.adjustment_value);
            lines.push(`Regra: ${rule.label} (${adj})`);
            lines.push("Selecione uma casa para ver o preço final");
          } else {
            lines.push("Sem regra ativa");
          }
          return (
            <div
              key={i}
              title={lines.join("\n")}
              data-testid={`seasonal-day-${iso}`}
              className={`aspect-square flex items-center justify-center text-[10px] rounded-sm cursor-help ${
                c ? `${c.bg} ${c.text} font-medium` : "bg-slate-50 text-slate-600"
              }`}
            >
              {d}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =========================== HouseStatsPanel =========================== */
function HouseStatsPanel({ house, rules }) {
  const stats = useMemo(() => {
    if (!house) return null;
    const base = Number(house.base_price) || 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(today);
    end.setFullYear(end.getFullYear() + 1);
    let min = Infinity, max = -Infinity, sum = 0, count = 0;
    let daysWithRule = 0;
    const cur = new Date(today);
    while (cur < end) {
      const iso = cur.toISOString().slice(0, 10);
      const rule = resolveRuleForDate(rules, iso, house);
      const nightly = computeNightlyLocal(base, rule);
      if (nightly < min) min = nightly;
      if (nightly > max) max = nightly;
      sum += nightly;
      count += 1;
      if (rule) daysWithRule += 1;
      cur.setDate(cur.getDate() + 1);
    }
    if (count === 0) return null;
    return {
      min, max, avg: sum / count, base,
      days_total: count, days_with_rule: daysWithRule,
    };
  }, [house, rules]);

  if (!stats) return null;

  const seasonalPct = ((stats.days_with_rule / stats.days_total) * 100).toFixed(0);

  return (
    <div
      className="mt-6 p-4 border border-[#0AA89C]/30 bg-[#0AA89C]/5 rounded-md"
      data-testid="seasonal-house-stats"
    >
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-[#0AA89C]" />
        <h3 className="font-semibold text-sm text-[#22302E]">
          Projeção 12 meses · {house.name}
        </h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBox label="Preço base" value={fmtBRL(stats.base)} testid="stat-base" />
        <StatBox label="Diária mínima" value={fmtBRL(stats.min)} testid="stat-min" tone="ok" />
        <StatBox label="Diária média" value={fmtBRL(stats.avg)} testid="stat-avg" />
        <StatBox label="Diária máxima" value={fmtBRL(stats.max)} testid="stat-max" tone="warn" />
      </div>
      <div className="mt-3 text-[11px] text-slate-600 flex items-start gap-1">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>
          {stats.days_with_rule} de {stats.days_total} dias ({seasonalPct}%) têm alguma
          regra sazonal aplicada. Cálculo local, mesma fórmula do backend.
        </span>
      </div>
    </div>
  );
}

function StatBox({ label, value, testid, tone }) {
  const toneClass = tone === "ok"
    ? "text-emerald-700"
    : tone === "warn"
      ? "text-amber-700"
      : "text-[#22302E]";
  return (
    <div className="bg-white border border-slate-200 rounded p-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-sm font-semibold mt-0.5 ${toneClass}`}>{value}</div>
    </div>
  );
}

/* ========================= PublicVerification ========================== */
/** Botão "Verificar no site público" — chama /public/availability com uma
 *  data-exemplo dentro do período da regra. Reaproveita o mesmo endpoint
 *  consumido pela página pública do hóspede. */
function PublicVerification({ rule, house }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  function pickSampleRange() {
    // check_in = today (if inside rule) OR rule.date_start; check_out = min(check_in+3d, rule.date_end)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const ds = new Date(rule.date_start + "T00:00:00");
    const de = new Date(rule.date_end + "T00:00:00");
    const ci = today > ds && today < de ? today : ds;
    const co = new Date(ci);
    co.setDate(co.getDate() + 3);
    // clamp check_out to date_end + 1 (exclusive), but ensure at least 1 night
    const maxCo = new Date(de); maxCo.setDate(maxCo.getDate() + 1);
    if (co > maxCo) co.setTime(maxCo.getTime());
    if (co <= ci) { co.setTime(ci.getTime()); co.setDate(co.getDate() + 1); }
    return {
      check_in: ci.toISOString().slice(0, 10),
      check_out: co.toISOString().slice(0, 10),
    };
  }

  async function onVerify() {
    if (!house) {
      toast.error("Nenhuma casa disponível para verificação.");
      return;
    }
    setOpen(true);
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { check_in, check_out } = pickSampleRange();
      const r = await api.post("/public/availability", {
        house_id: house.id,
        check_in,
        check_out,
      });
      setResult({ ...r.data, check_in, check_out });
    } catch (e) {
      setError(e?.response?.data?.detail || "Falha ao consultar site público.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={onVerify}
        disabled={!house}
        className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded bg-white/70 hover:bg-white border border-slate-200 text-[#0AA89C] disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={`seasonal-verify-public-${rule.id}`}
        title={house ? undefined : "Nenhuma casa disponível para verificação"}
      >
        <Globe className="w-3 h-3" /> Verificar no site público
      </button>

      {open && (
        <div
          className="mt-2 p-3 bg-white border border-slate-200 rounded-md text-xs"
          data-testid={`seasonal-verify-result-${rule.id}`}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              Resultado /public/availability
            </div>
            <button
              onClick={() => { setOpen(false); setResult(null); setError(null); }}
              className="p-0.5 rounded hover:bg-slate-100 text-slate-500"
              data-testid={`seasonal-verify-close-${rule.id}`}
              type="button"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
          {loading && <div className="text-slate-500">Consultando…</div>}
          {error && <div className="text-rose-700">{error}</div>}
          {result && (() => {
            const nightlyAvg = result.nights > 0 ? result.base_total / result.nights : 0;
            const seasonal = Math.abs(nightlyAvg - Number(house.base_price)) > 0.5;
            return (
              <div className="space-y-1.5">
                <div className="text-slate-600">
                  Casa: <strong className="text-[#22302E]">{house.name}</strong>
                </div>
                <div className="text-slate-600">
                  Período de teste: <strong className="text-[#22302E]">{result.check_in} → {result.check_out}</strong> ({result.nights} noite{result.nights !== 1 ? "s" : ""})
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <StatBox label="Diária média" value={fmtBRL(nightlyAvg)} testid={`verify-avg-${rule.id}`} />
                  <StatBox label="Total (com limpeza)" value={fmtBRL(result.total)} testid={`verify-total-${rule.id}`} />
                </div>
                {seasonal ? (
                  <div
                    className="text-[10px] px-2 py-1 rounded bg-[#0AA89C]/10 border border-[#0AA89C]/30 text-[#0AA89C]"
                    data-testid={`verify-seasonal-badge-${rule.id}`}
                  >
                    ✓ Sazonalidade aplicada · base R$ {Number(house.base_price).toLocaleString("pt-BR", { minimumFractionDigits: 2 })} → média {fmtBRL(nightlyAvg)}
                  </div>
                ) : (
                  <div
                    className="text-[10px] px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800"
                    data-testid={`verify-no-seasonal-${rule.id}`}
                  >
                    ⚠ Preço igual ao base — a regra pode não estar aplicando neste período (verifique escopo/prioridade).
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

/* ============================== RuleModal ============================== */
function RuleModal({ rule, houses, onClose, onSaved }) {
  const isNew = !!rule._new;
  const [form, setForm] = useState(rule);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewHouseId, setPreviewHouseId] = useState("");

  // Auto-select first house for preview if scope=house.
  useEffect(() => {
    if (form.scope_type === "house" && form.scope_id) {
      setPreviewHouseId(form.scope_id);
    } else if (!previewHouseId && houses.length > 0) {
      setPreviewHouseId(houses[0].id);
    }
  }, [form.scope_type, form.scope_id, houses, previewHouseId]);

  // Debounced live preview.
  useEffect(() => {
    if (!previewHouseId || !form.date_start || !form.date_end) {
      setPreview(null);
      return;
    }
    if (form.date_end < form.date_start) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      // Build draft rule from current form values so the backend can preview
      // an unsaved rule. We skip the draft if the form isn't in a valid
      // enough state to compute (missing scope_id for house/group).
      const adjVal = Number(form.adjustment_value);
      const validDraft =
        !!form.date_start && !!form.date_end &&
        form.date_end >= form.date_start &&
        !Number.isNaN(adjVal) &&
        (form.scope_type === "global" || !!form.scope_id);

      const draft_rule = validDraft ? {
        id: form.id || "__draft__",
        active: true,
        label: form.label || "(rascunho)",
        scope_type: form.scope_type,
        scope_id: form.scope_type === "global" ? null : form.scope_id,
        date_start: form.date_start,
        date_end: form.date_end,
        adjustment_type: form.adjustment_type,
        adjustment_value: adjVal,
        min_price: form.min_price === "" || form.min_price == null ? null : Number(form.min_price),
        max_price: form.max_price === "" || form.max_price == null ? null : Number(form.max_price),
        priority: Number(form.priority) || 0,
      } : null;

      setPreviewLoading(true);
      try {
        const check_in = form.date_start;
        // preview endpoint expects check_out as exclusive; use day-after for a 1-night preview minimum.
        // We use date_end + 1 day so per_night includes date_end.
        const end = new Date(form.date_end + "T00:00:00");
        end.setDate(end.getDate() + 1);
        const check_out = end.toISOString().slice(0, 10);
        const r = await api.post("/admin/seasonal-pricing/preview", {
          house_id: previewHouseId,
          check_in,
          check_out,
          draft_rule,
        });
        if (!cancelled) setPreview(r.data);
      } catch (e) {
        if (!cancelled) setPreview({ error: e?.response?.data?.detail || "Falha no preview." });
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [previewHouseId, form.date_start, form.date_end, form.adjustment_type, form.adjustment_value, form.min_price, form.max_price, form.priority, form.scope_type, form.scope_id]);

  function set(patch) { setForm((f) => ({ ...f, ...patch })); }

  function validate() {
    if (!form.label.trim()) return "Informe um rótulo para a regra.";
    if (!form.date_start || !form.date_end) return "Informe as datas de início e fim.";
    if (form.date_end < form.date_start) return "A data final deve ser igual ou posterior à inicial.";
    if (form.scope_type !== "global" && !form.scope_id) return "Selecione o escopo (casa ou grupo).";
    if (form.adjustment_type === "percent") {
      const v = Number(form.adjustment_value);
      if (Number.isNaN(v)) return "Valor de ajuste inválido.";
      if (v <= -100) return "Ajuste percentual deve ser maior que -100%.";
    } else {
      if (Number.isNaN(Number(form.adjustment_value))) return "Valor de ajuste inválido.";
    }
    return null;
  }

  async function onSubmit() {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        label: form.label.trim(),
        scope_type: form.scope_type,
        scope_id: form.scope_type === "global" ? null : (form.scope_id || null),
        date_start: form.date_start,
        date_end: form.date_end,
        adjustment_type: form.adjustment_type,
        adjustment_value: Number(form.adjustment_value),
        min_price: form.min_price === "" || form.min_price == null ? null : Number(form.min_price),
        max_price: form.max_price === "" || form.max_price == null ? null : Number(form.max_price),
        priority: Number(form.priority) || 0,
      };
      if (isNew) {
        await api.post("/admin/seasonal-pricing/rules", payload);
        toast.success("Regra criada.");
      } else {
        await api.put(`/admin/seasonal-pricing/rules/${form.id}`, payload);
        toast.success("Regra atualizada.");
      }
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar regra.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="seasonal-rule-modal">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nova regra sazonal" : "Editar regra"}</DialogTitle>
          <DialogDescription>
            Configure escopo, período e ajuste. O painel à direita mostra o preço final
            calculado em tempo real para a casa selecionada.
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 mt-2">
          {/* Left: form */}
          <div className="space-y-4">
            <div>
              <Label>Rótulo *</Label>
              <Input
                value={form.label}
                onChange={(e) => set({ label: e.target.value })}
                placeholder='Ex.: "Alta temporada — Verão 2026"'
                data-testid="seasonal-modal-label"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Escopo *</Label>
                <Select
                  value={form.scope_type}
                  onValueChange={(v) => set({ scope_type: v, scope_id: v === "global" ? null : "" })}
                >
                  <SelectTrigger data-testid="seasonal-modal-scope-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global (todas as casas)</SelectItem>
                    <SelectItem value="house">Casa específica</SelectItem>
                    <SelectItem value="group">Grupo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.scope_type === "house" && (
                <div>
                  <Label>Casa *</Label>
                  <Select value={form.scope_id || ""} onValueChange={(v) => set({ scope_id: v })}>
                    <SelectTrigger data-testid="seasonal-modal-scope-house"><SelectValue placeholder="Selecionar…" /></SelectTrigger>
                    <SelectContent>
                      {houses.map((h) => (
                        <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {form.scope_type === "group" && (
                <div>
                  <Label>ID do grupo *</Label>
                  <Input
                    value={form.scope_id || ""}
                    onChange={(e) => set({ scope_id: e.target.value })}
                    placeholder="grupo-praia"
                    data-testid="seasonal-modal-scope-group"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Início *</Label>
                <Input
                  type="date" value={form.date_start}
                  onChange={(e) => set({ date_start: e.target.value })}
                  data-testid="seasonal-modal-date-start"
                />
              </div>
              <div>
                <Label>Fim *</Label>
                <Input
                  type="date" value={form.date_end}
                  onChange={(e) => set({ date_end: e.target.value })}
                  data-testid="seasonal-modal-date-end"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo de ajuste *</Label>
                <Select
                  value={form.adjustment_type}
                  onValueChange={(v) => set({ adjustment_type: v })}
                >
                  <SelectTrigger data-testid="seasonal-modal-adj-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentual (%)</SelectItem>
                    <SelectItem value="fixed">Valor fixo (R$)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{form.adjustment_type === "percent" ? "Ajuste (%)" : "Ajuste (R$)"} *</Label>
                <Input
                  type="number" step={form.adjustment_type === "percent" ? "0.1" : "1"}
                  value={form.adjustment_value}
                  onChange={(e) => set({ adjustment_value: e.target.value })}
                  data-testid="seasonal-modal-adj-value"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Mín. R$</Label>
                <Input
                  type="number" step="0.01" value={form.min_price}
                  onChange={(e) => set({ min_price: e.target.value })}
                  placeholder="opcional"
                  data-testid="seasonal-modal-min-price"
                />
              </div>
              <div>
                <Label>Máx. R$</Label>
                <Input
                  type="number" step="0.01" value={form.max_price}
                  onChange={(e) => set({ max_price: e.target.value })}
                  placeholder="opcional"
                  data-testid="seasonal-modal-max-price"
                />
              </div>
              <div>
                <Label>Prioridade</Label>
                <Input
                  type="number" value={form.priority}
                  onChange={(e) => set({ priority: e.target.value })}
                  data-testid="seasonal-modal-priority"
                />
              </div>
            </div>

            <div className="text-xs text-slate-500 flex items-start gap-2">
              <ShieldAlert className="w-4 h-4 text-slate-400 mt-0.5" />
              <span>
                Casas &gt; grupo &gt; global. Em caso de empate, o maior número em
                prioridade vence. Min/máx são aplicados após o ajuste.
              </span>
            </div>
          </div>

          {/* Right: preview */}
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-slate-500">Preview em tempo real</div>
            </div>

            <div className="mb-3">
              <Label className="text-xs">Casa para simulação</Label>
              <Select value={previewHouseId} onValueChange={setPreviewHouseId}>
                <SelectTrigger data-testid="seasonal-modal-preview-house"><SelectValue placeholder="Selecionar…" /></SelectTrigger>
                <SelectContent>
                  {houses.map((h) => (
                    <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {previewLoading && <div className="text-xs text-slate-500">Calculando…</div>}

            {!previewLoading && preview?.error && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                {preview.error}
              </div>
            )}

            {!previewLoading && preview && !preview.error && (
              <div data-testid="seasonal-modal-preview-result">
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-white border border-slate-200 rounded p-2">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Noites</div>
                    <div className="text-sm font-semibold">{preview.nights}</div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded p-2">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Total base</div>
                    <div className="text-sm font-semibold">{fmtBRL(preview.base_total)}</div>
                  </div>
                </div>

                <div className="max-h-64 overflow-y-auto border border-slate-200 rounded bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 text-slate-500 uppercase text-[10px]">
                      <tr>
                        <th className="px-2 py-1 text-left">Data</th>
                        <th className="px-2 py-1 text-right">Diária</th>
                        <th className="px-2 py-1 text-left">Regra</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.per_night || []).map((n) => (
                        <tr key={n.date} className="border-t border-slate-100">
                          <td className="px-2 py-1 font-mono">{n.date}</td>
                          <td className="px-2 py-1 text-right">{fmtBRL(n.nightly)}</td>
                          <td className="px-2 py-1 text-slate-500">
                            {n.rule_id
                              ? (n.rule_id === (form.id || "__draft__") ? "esta regra (rascunho)" : "outra regra")
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-[11px] text-slate-500 mt-2">
                  <Info className="inline w-3 h-3 mr-1" />
                  Preview inclui a regra do formulário (rascunho) e todas as regras
                  já salvas. Nada é gravado até você clicar em <strong>Salvar</strong>.
                </p>
              </div>
            )}

            {!previewLoading && !preview && (
              <div className="text-xs text-slate-500">
                Preencha datas e selecione uma casa para visualizar.
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} data-testid="seasonal-modal-cancel">
            Cancelar
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving}
            className="bg-[#0AA89C] hover:bg-[#088c82] text-white"
            data-testid="seasonal-modal-save"
          >
            {saving ? "Salvando…" : isNew ? "Criar regra" : "Salvar alterações"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
